import React, { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Search,
  RefreshCw,
  Package,
  Pencil,
  Tags,
  Trash2,
  X,
  Image as ImageIcon,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Sparkles,
  Boxes,
  Layers3,
  Repeat2,
  ShoppingBag,
  IndianRupee,
} from "lucide-react";
import useStore from "../../Sevices/useStore";

const initialForm = {
  itemId: "",
  itemName: "",
  category: "",
  size: "Normal",
  description: "",
  isReturnable: "Yes",
  price: "",
  image: [],
  color: "",
};

function Store() {
  const {
    fetchStoreItemsApi,
    fetchStoreCategoriesApi,
    addStoreCategoryApi,
    deleteStoreCategoryApi,
    saveStoreItemApi,
  } = useStore();

  const [loading, setLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [categories, setCategories] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");

  const [showItemModal, setShowItemModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);

  const [formData, setFormData] = useState(initialForm);
  const [newCategory, setNewCategory] = useState("");

  const [selectedCardImages, setSelectedCardImages] = useState({});
  const [selectedModalImageIndex, setSelectedModalImageIndex] = useState(0);

  const [viewerImages, setViewerImages] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(0);

  const loadStoreData = () => {
    fetchStoreItemsApi(setItems, setFilteredItems, setLoading);
    fetchStoreCategoriesApi(setCategories, setCategoryLoading);
  };

  useEffect(() => {
    loadStoreData();
  }, []);

  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      setFilteredItems(items);
      return;
    }

    const filtered = items.filter((item) => {
      return (
        item?.item_name?.toLowerCase()?.includes(term) ||
        item?.description?.toLowerCase()?.includes(term) ||
        item?.color?.toLowerCase()?.includes(term) ||
        item?.size?.toLowerCase()?.includes(term) ||
        item?.category?.toLowerCase()?.includes(term)
      );
    });

    setFilteredItems(filtered);
  }, [searchTerm, items]);

  const categoryOptions = useMemo(() => {
    return (categories || []).map((cat) => ({
      label: cat.category_name,
      value: cat.category_name,
      id: cat.pk_category_id,
    }));
  }, [categories]);

  const resetForm = () => {
    setFormData(initialForm);
    setSelectedModalImageIndex(0);
  };

  const openAddItemModal = () => {
    resetForm();
    setShowItemModal(true);
  };

  const openEditItemModal = (item) => {
    setFormData({
      itemId: item?.pk_item_id || "",
      itemName: item?.item_name || "",
      category: item?.category || "",
      size: item?.size || "Normal",
      description: item?.description || "",
      isReturnable: item?.is_returnable || "Yes",
      price: item?.price || "",
      image: (item?.imageList || []).map((url) => ({
        preview: url,
        url,
        isBase64: false,
      })),
      color: item?.color || "",
    });

    setSelectedModalImageIndex(0);
    setShowItemModal(true);
  };

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

 const handleImageChange = async (e) => {
   const files = Array.from(e.target.files || []);

   if (!files.length) return;

   try {
     const selectedImages = files.map((file) => {
       if (!file.type.startsWith("image/")) {
         throw new Error(`${file.name} is not a valid image`);
       }

       if (file.size > 5 * 1024 * 1024) {
         throw new Error(`${file.name} size should be less than 5MB`);
       }

       return {
         file,
         fileName: file.name,
         preview: URL.createObjectURL(file),
         mimeType: file.type,
         isNew: true,
       };
     });

     setFormData((prev) => ({
       ...prev,
       image: [
         ...(Array.isArray(prev.image) ? prev.image : []),
         ...selectedImages,
       ],
     }));

     console.log("Selected image objects:", selectedImages);
   } catch (error) {
     console.error("handleImageChange error:", error);

     alert(error?.message || "Unable to process image");
   } finally {
     e.target.value = "";
   }
 };

  const removeImage = (index) => {
    setFormData((prev) => {
      const updated = prev.image.filter((_, i) => i !== index);

      if (selectedModalImageIndex >= updated.length) {
        setSelectedModalImageIndex(Math.max(0, updated.length - 1));
      }

      return {
        ...prev,
        image: updated,
      };
    });
  };

  const handleSaveItem = async () => {
    if (saving) return;

    if (!String(formData.itemName || "").trim()) {
      alert("Item name is required");
      return;
    }

    if (!Array.isArray(formData.image) || formData.image.length === 0) {
      alert("Please select at least one image");
      return;
    }

    setSaving(true);

    await saveStoreItemApi({
      itemData: {
        ...formData,
        image: formData.image,
      },

      onSuccess: async () => {
        setShowItemModal(false);
        resetForm();
        loadStoreData();
      },

      onFinally: () => {
        setSaving(false);
      },
    });
  };

  const handleAddCategory = () => {
    if (!newCategory.trim()) return;

    addStoreCategoryApi(newCategory.trim(), () => {
      setNewCategory("");
      fetchStoreCategoriesApi(setCategories, setCategoryLoading);
    });
  };

  const handleDeleteCategory = (categoryId) => {
    deleteStoreCategoryApi(categoryId, () => {
      fetchStoreCategoriesApi(setCategories, setCategoryLoading);
    });
  };

  const openViewer = (images = [], index = 0) => {
    if (!images.length) return;

    setViewerImages(images);
    setViewerIndex(index);
    setShowImageViewer(true);
  };

  const getCardSelectedIndex = (itemId, total) => {
    const current = selectedCardImages[itemId] ?? 0;

    if (current > total - 1) return 0;

    return current;
  };

  const setCardSelectedIndex = (itemId, index) => {
    setSelectedCardImages((prev) => ({
      ...prev,
      [itemId]: index,
    }));
  };

  const nextViewerImage = () => {
    setViewerIndex((prev) => (prev + 1) % viewerImages.length);
  };

  const prevViewerImage = () => {
    setViewerIndex(
      (prev) => (prev - 1 + viewerImages.length) % viewerImages.length,
    );
  };

  const modalMainImage =
    formData.image?.[selectedModalImageIndex]?.preview ||
    formData.image?.[selectedModalImageIndex]?.url ||
    formData.image?.[selectedModalImageIndex] ||
    "";

  return (
    <div className="min-h-screen space-y-4 bg-slate-50 p-4">
      <style>{`
        .store-shimmer {
          position: relative;
          overflow: hidden;
          background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 37%, #f1f5f9 63%);
          background-size: 400% 100%;
          animation: shimmer 1.4s ease infinite;
        }

        @keyframes shimmer {
          0% { background-position: 100% 50%; }
          100% { background-position: 0 50%; }
        }

        .store-float {
          transition: transform .24s ease, box-shadow .24s ease, border-color .24s ease;
        }

        .store-float:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
          border-color: #fdba74;
        }

        .store-scale {
          transition: transform .28s ease;
        }

        .store-scale:hover {
          transform: scale(1.02);
        }

        .store-thumb-active {
          box-shadow: 0 0 0 2px #f97316 inset;
          border-color: #fb923c !important;
          background: #fff7ed;
        }
      `}</style>

      <div className="relative overflow-hidden rounded-[24px] border border-orange-200 bg-gradient-to-br from-orange-500 via-orange-400 to-amber-300 p-5 shadow-sm">
        <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/20 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-32 w-32 rounded-full bg-emerald-400/20 blur-3xl" />

        <div className="relative flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/20 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
              <Sparkles size={14} />
              Packky Store Management
            </div>

            <h2 className="mt-3 text-2xl font-black tracking-tight text-white">
              Store Inventory
            </h2>

            <p className="mt-1 max-w-2xl text-sm leading-6 text-orange-50">
              Manage store products, pricing, returnable items, categories and
              image galleries from one clean inventory dashboard.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setShowCategoryModal(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white px-4 py-2 text-sm font-semibold text-orange-600 shadow-sm transition hover:bg-orange-50"
            >
              <Tags size={16} />
              Categories
            </button>

            <button
              onClick={loadStoreData}
              className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-orange-50"
            >
              <RefreshCw size={16} />
              Refresh
            </button>

            <button
              onClick={openAddItemModal}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-500 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700"
            >
              <Plus size={16} />
              Add Item
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          icon={Boxes}
          label="Total Items"
          value={items.length}
          helper="All products in store"
        />

        <MetricCard
          icon={Layers3}
          label="Categories"
          value={categories.length}
          helper="Mapped product groups"
        />

        <MetricCard
          icon={Repeat2}
          label="Returnable Items"
          value={
            items.filter(
              (i) => String(i?.is_returnable).toLowerCase() === "yes",
            ).length
          }
          helper="Eligible for returns"
        />

        <MetricCard
          icon={ShoppingBag}
          label="Visible Items"
          value={filteredItems.length}
          helper="Current filtered result"
        />
      </div>

      <div className="rounded-2xl border border-orange-100 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-900">
              Product Catalogue
            </h3>
            <p className="text-xs text-slate-500">
              Search by item name, category, color, size or description.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-[360px]">
              <Search
                size={17}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-4 text-sm font-medium outline-none transition focus:border-orange-300 focus:bg-white focus:ring-4 focus:ring-orange-50"
              />
            </div>

            <div className="rounded-xl border border-orange-100 bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-700">
              {filteredItems.length} result
              {filteredItems.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-orange-100 bg-white p-3 shadow-sm">
        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="overflow-hidden rounded-2xl border border-orange-100 bg-white p-3"
              >
                <div className="store-shimmer h-48 rounded-xl" />
                <div className="mt-3 store-shimmer h-5 w-2/3 rounded-lg" />
                <div className="mt-2 store-shimmer h-4 w-full rounded-lg" />
                <div className="mt-2 store-shimmer h-4 w-5/6 rounded-lg" />
                <div className="mt-3 flex gap-2">
                  <div className="store-shimmer h-7 w-16 rounded-full" />
                  <div className="store-shimmer h-7 w-16 rounded-full" />
                  <div className="store-shimmer h-7 w-16 rounded-full" />
                </div>
                <div className="mt-3 flex gap-2">
                  <div className="store-shimmer h-11 w-11 rounded-lg" />
                  <div className="store-shimmer h-11 w-11 rounded-lg" />
                  <div className="store-shimmer h-11 w-11 rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Package size={42} className="text-orange-400" />
            <h3 className="mt-4 text-lg font-semibold text-slate-900">
              No store items found
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Add your first item or adjust the search filter.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
            {filteredItems.map((item) => {
              const images = item?.imageList || [];
              const selectedIndex = getCardSelectedIndex(
                item.pk_item_id,
                images.length || 1,
              );
              const previewImage = images?.[selectedIndex] || images?.[0] || "";

              return (
                <div
                  key={item.pk_item_id}
                  className="store-float overflow-hidden rounded-2xl border border-orange-100 bg-white shadow-sm"
                >
                  <div className="relative h-42 overflow-hidden bg-gradient-to-br from-orange-50 to-amber-50">
                    {previewImage ? (
                      <img
                        src={previewImage}
                        alt={item.item_name}
                        className="store-scale h-full w-full object-contain p-2.5"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-slate-400">
                        <ImageIcon size={40} />
                      </div>
                    )}

                    <div className="absolute left-2 top-2 flex max-w-[calc(100%-70px)] flex-wrap items-center gap-1.5">
                      <span className="rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-orange-600 shadow-sm">
                        ₹ {Number(item.price || 0).toFixed(2)}
                      </span>

                      <span
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold shadow-sm ${
                          String(item.is_returnable).toLowerCase() === "yes"
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-red-50 text-red-700"
                        }`}
                      >
                        {item.is_returnable === "Yes"
                          ? "Returnable"
                          : "Non Returnable"}
                      </span>
                    </div>

                    <div className="absolute right-2 top-2 flex items-center gap-1.5">
                      <button
                        onClick={() => openViewer(images, selectedIndex)}
                        className="rounded-lg bg-white/95 p-1.5 text-slate-700 shadow-sm transition hover:bg-white"
                        title="View images"
                      >
                        <Eye size={15} />
                      </button>

                      <button
                        onClick={() => openEditItemModal(item)}
                        className="rounded-lg bg-white/95 p-1.5 text-slate-700 shadow-sm transition hover:bg-white"
                        title="Edit item"
                      >
                        <Pencil size={15} />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2.5 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="truncate text-sm font-bold text-slate-900">
                          {item.item_name}
                        </h3>
                        <p className="mt-0.5 text-[11px] font-medium text-slate-500">
                          Item ID: #{item.pk_item_id}
                        </p>
                      </div>
                    </div>

                    <p className="line-clamp-2 text-xs leading-5 text-slate-600">
                      {item.description || "No description available"}
                    </p>

                    <div className="flex flex-wrap gap-1.5">
                      <span className="rounded-full bg-orange-50 px-2.5 py-0.5 text-[11px] font-semibold text-orange-700">
                        Size: {item.size || "-"}
                      </span>

                      <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-700">
                        Color: {item.color || "-"}
                      </span>

                      {item?.category ? (
                        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-700">
                          {item.category}
                        </span>
                      ) : null}
                    </div>

                    {!!images.length && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            Images
                          </p>
                          <p className="text-[11px] text-slate-500">
                            {selectedIndex + 1} / {images.length}
                          </p>
                        </div>

                        <div className="flex gap-1.5 overflow-x-auto pb-1">
                          {images.map((img, index) => (
                            <button
                              key={index}
                              onClick={() =>
                                setCardSelectedIndex(item.pk_item_id, index)
                              }
                              className={`h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg border transition ${
                                selectedIndex === index
                                  ? "store-thumb-active"
                                  : "border-slate-200 bg-white hover:border-orange-300"
                              }`}
                            >
                              <img
                                src={img}
                                alt={`thumb-${index}`}
                                className="h-full w-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showItemModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 backdrop-blur-[2px]">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-[24px] bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white/95 px-5 py-3 backdrop-blur">
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  {formData.itemId ? "Edit Store Item" : "Add Store Item"}
                </h3>
                <p className="text-xs text-slate-500">
                  Manage pricing, category, returnable status and image gallery.
                </p>
              </div>

              <button
                onClick={() => setShowItemModal(false)}
                className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-4 p-4 xl:grid-cols-[1.1fr_0.9fr]">
              <div className="space-y-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                      Item Name
                    </label>
                    <input
                      type="text"
                      value={formData.itemName}
                      onChange={(e) =>
                        handleInputChange("itemName", e.target.value)
                      }
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-orange-400"
                      placeholder="Enter item name"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                      Category
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) =>
                        handleInputChange("category", e.target.value)
                      }
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-orange-400"
                    >
                      <option value="">Select category</option>
                      {categoryOptions.map((cat) => (
                        <option key={cat.id} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                      Size
                    </label>
                    <input
                      type="text"
                      value={formData.size}
                      onChange={(e) =>
                        handleInputChange("size", e.target.value)
                      }
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-orange-400"
                      placeholder="Normal / XL / Large"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                      Color
                    </label>
                    <input
                      type="text"
                      value={formData.color}
                      onChange={(e) =>
                        handleInputChange("color", e.target.value)
                      }
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-orange-400"
                      placeholder="Blue / Black / White"
                    />
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                      Price
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) =>
                        handleInputChange("price", e.target.value)
                      }
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-orange-400"
                      placeholder="Enter price"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                      Returnable
                    </label>
                    <select
                      value={formData.isReturnable}
                      onChange={(e) =>
                        handleInputChange("isReturnable", e.target.value)
                      }
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-orange-400"
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-400"
                    placeholder="Enter item description"
                  />
                </div>

                <div className="rounded-2xl border border-dashed border-orange-200 bg-orange-50/60 p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900">
                        Upload Images
                      </h4>
                      <p className="text-xs text-slate-500">
                        Add multiple images and choose preview.
                      </p>
                    </div>

                    <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-orange-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-orange-600">
                      <Plus size={15} />
                      Add Images
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleImageChange}
                      />
                    </label>
                  </div>

                  {formData.image.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {formData.image.map((img, index) => {
                        const thumb = img.preview || img.url || img;
                        const active = selectedModalImageIndex === index;

                        return (
                          <div key={index} className="relative">
                            <button
                              onClick={() => setSelectedModalImageIndex(index)}
                              className={`h-14 w-14 overflow-hidden rounded-xl border transition ${
                                active
                                  ? "store-thumb-active"
                                  : "border-slate-200 bg-white hover:border-orange-300"
                              }`}
                            >
                              <img
                                src={thumb}
                                alt={`modal-thumb-${index}`}
                                className="h-full w-full object-cover"
                              />
                            </button>

                            <button
                              onClick={() => removeImage(index)}
                              className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-1 text-red-500 shadow"
                            >
                              <Trash2 size={11} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div className="rounded-2xl border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-slate-50 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-900">
                      Selected Image Preview
                    </h4>

                    {formData.image.length > 0 && (
                      <button
                        onClick={() =>
                          openViewer(
                            formData.image.map(
                              (img) => img.preview || img.url || img,
                            ),
                            selectedModalImageIndex,
                          )
                        }
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        <Eye size={13} />
                        View
                      </button>
                    )}
                  </div>

                  <div className="relative flex h-[220px] items-center justify-center overflow-hidden rounded-2xl bg-white">
                    {modalMainImage ? (
                      <img
                        src={modalMainImage}
                        alt="Selected preview"
                        className="max-h-full max-w-full object-contain p-3"
                      />
                    ) : (
                      <div className="text-center text-slate-400">
                        <ImageIcon size={34} className="mx-auto" />
                        <p className="mt-2 text-xs">No image selected</p>
                      </div>
                    )}
                  </div>

                  {formData.image.length > 1 && (
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <button
                        onClick={() =>
                          setSelectedModalImageIndex((prev) =>
                            prev === 0 ? formData.image.length - 1 : prev - 1,
                          )
                        }
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        <ChevronLeft size={14} />
                        Prev
                      </button>

                      <p className="text-xs text-slate-500">
                        {selectedModalImageIndex + 1} / {formData.image.length}
                      </p>

                      <button
                        onClick={() =>
                          setSelectedModalImageIndex((prev) =>
                            prev === formData.image.length - 1 ? 0 : prev + 1,
                          )
                        }
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Next
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border bg-white p-3 shadow-sm">
                  <h4 className="text-sm font-semibold text-slate-900">
                    Live Product Preview
                  </h4>

                  <div className="mt-3 overflow-hidden rounded-2xl border border-orange-100 bg-[#FFF7ED]">
                    <div className="flex h-36 items-center justify-center bg-white/70">
                      {modalMainImage ? (
                        <img
                          src={modalMainImage}
                          alt="live-preview"
                          className="max-h-full max-w-full object-contain p-3"
                        />
                      ) : (
                        <ImageIcon size={30} className="text-slate-300" />
                      )}
                    </div>

                    <div className="space-y-2 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h5 className="truncate text-sm font-bold text-slate-900">
                            {formData.itemName || "Item name"}
                          </h5>

                          <p className="text-xs text-slate-500">
                            {formData.category || "No category selected"}
                          </p>
                        </div>

                        <span className="rounded-full bg-orange-100 px-2.5 py-0.5 text-[11px] font-semibold text-orange-700">
                          ₹ {formData.price || "0.00"}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-1.5">
                        <span className="rounded-full bg-white px-2.5 py-0.5 text-[11px] font-semibold text-slate-700">
                          {formData.size || "Normal"}
                        </span>

                        <span className="rounded-full bg-white px-2.5 py-0.5 text-[11px] font-semibold text-slate-700">
                          {formData.color || "No color"}
                        </span>

                        <span
                          className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                            formData.isReturnable === "Yes"
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-red-50 text-red-700"
                          }`}
                        >
                          {formData.isReturnable === "Yes"
                            ? "Returnable"
                            : "Non Returnable"}
                        </span>
                      </div>

                      <p className="line-clamp-2 text-xs leading-5 text-slate-600">
                        {formData.description ||
                          "Item description will appear here."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t bg-white px-5 py-3">
              <button
                onClick={resetForm}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <RotateCcw size={15} />
                Reset
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowItemModal(false)}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Cancel
                </button>

                <button
                  onClick={handleSaveItem}
                  disabled={saving}
                  className="rounded-xl bg-orange-500 px-4 py-2 text-xs font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {saving
                    ? "Saving..."
                    : formData.itemId
                      ? "Update Item"
                      : "Save Item"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-2xl rounded-[30px] bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h3 className="text-xl font-bold text-slate-900">
                  Manage Categories
                </h3>
                <p className="text-sm text-slate-500">
                  Add and remove categories used in store item mapping.
                </p>
              </div>

              <button
                onClick={() => setShowCategoryModal(false)}
                className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4 p-6">
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Enter category name"
                  className="h-11 flex-1 rounded-xl border border-slate-200 px-4 text-sm outline-none focus:border-orange-400"
                />

                <button
                  onClick={handleAddCategory}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
                >
                  <Plus size={16} />
                  Add Category
                </button>
              </div>

              <div className="max-h-[420px] overflow-y-auto rounded-2xl border border-orange-100">
                {categoryLoading ? (
                  <div className="space-y-3 p-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="store-shimmer h-16 rounded-2xl" />
                    ))}
                  </div>
                ) : categories.length === 0 ? (
                  <div className="p-10 text-center text-sm text-slate-500">
                    No categories found
                  </div>
                ) : (
                  <div className="divide-y divide-orange-50">
                    {categories.map((cat) => (
                      <div
                        key={cat.pk_category_id}
                        className="flex items-center justify-between gap-3 px-4 py-4 transition hover:bg-orange-50/50"
                      >
                        <div>
                          <p className="font-semibold text-slate-900">
                            {cat.category_name}
                          </p>
                          <p className="text-xs text-slate-500">
                            ID: {cat.pk_category_id}
                          </p>
                        </div>

                        <button
                          onClick={() =>
                            handleDeleteCategory(cat.pk_category_id)
                          }
                          className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showImageViewer && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
          <div className="relative w-full max-w-4xl rounded-[24px] bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  Image Viewer
                </h3>
                <p className="text-xs text-slate-500">
                  {viewerImages.length
                    ? `${viewerIndex + 1} / ${viewerImages.length}`
                    : ""}
                </p>
              </div>

              <button
                onClick={() => setShowImageViewer(false)}
                className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100"
              >
                <X size={18} />
              </button>
            </div>

            <div className="relative flex h-[58vh] items-center justify-center overflow-hidden rounded-b-[24px] bg-slate-50">
              {viewerImages?.[viewerIndex] ? (
                <img
                  src={viewerImages[viewerIndex]}
                  alt="viewer"
                  className="max-h-full max-w-full object-contain p-4"
                />
              ) : null}

              {viewerImages.length > 1 && (
                <>
                  <button
                    onClick={prevViewerImage}
                    className="absolute left-4 rounded-full bg-white p-2.5 text-slate-700 shadow-lg transition hover:scale-105"
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <button
                    onClick={nextViewerImage}
                    className="absolute right-4 rounded-full bg-white p-2.5 text-slate-700 shadow-lg transition hover:scale-105"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}
            </div>

            {viewerImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto border-t bg-white px-5 py-3">
                {viewerImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setViewerIndex(index)}
                    className={`h-14 w-14 flex-shrink-0 overflow-hidden rounded-xl border ${
                      viewerIndex === index
                        ? "store-thumb-active"
                        : "border-slate-200 hover:border-orange-300"
                    }`}
                  >
                    <img
                      src={img}
                      alt={`viewer-thumb-${index}`}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const MetricCard = ({ icon: Icon, label, value, helper }) => (
  <div className="group rounded-2xl border border-orange-100 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </p>

        <p className="mt-1 text-2xl font-black text-slate-950">{value}</p>

        <p className="mt-0.5 text-[11px] text-slate-400">{helper}</p>
      </div>

      <div className="rounded-xl bg-orange-50 p-2.5 text-orange-600 transition group-hover:bg-orange-500 group-hover:text-white">
        <Icon size={18} />
      </div>
    </div>
  </div>
);

export default Store;